<style scoped>
.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.form-row > div {
    flex: 1;
}

.articles-section {
    margin-top: 30px;
}

.articles-section h3 {
    font-size: 16px;
    color: #2c3e50;
    margin-bottom: 15px;
}

.article-selector {
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
    align-items: flex-end;
}

.article-selector > div {
    flex: 1;
}

.article-selector .add-article-btn {
    flex: 0 0 auto;
}

.table-articles {
    margin: 20px 0;
    max-height: 600px;
    overflow-y: auto;
    display: block;
}

.table-articles thead,
.table-articles tbody,
.table-articles tfoot {
    display: table;
    width: 100%;
    table-layout: fixed;
}

.table-articles td,
.table-articles th {
    padding: 6px 20px;
    text-align: left;
}

.table-articles th {
    font-weight: 600;
    font-size: 13px;
    font-style: italic;
    color: #2c3e50;
    padding-bottom: 10px;
}

.table-articles tbody tr {
    border-bottom: 1px solid rgb(230, 235, 240);
}

.table-articles tbody td {
    font-weight: 500;
    font-size: 13px;
    color: rgb(30, 40, 50);
    vertical-align: middle;
}

.table-articles :deep(.inputeo) {
    margin: 0px;
}

.table-articles .total-row {
    border-top: 2px solid #e67e22;
}

.table-articles .total-row td {
    padding-top: 15px;
    font-weight: bold;
    font-size: 1.05em;
    color: #2c3e50;
}

.table-articles .total-row td:first-child {
    text-align: right;
}

.delete-btn {
    background: none;
    border: none;
    color: #e74c3c;
    cursor: pointer;
    font-size: 16px;
    padding: 5px;
}

.delete-btn:hover {
    color: #c0392b;
}

.error-message {
    color: #e74c3c;
    font-size: 12px;
    margin-top: 5px;
}

#modal-multi-affair-reception :deep(.modal-content) {
    max-height: 90vh;
}

#modal-multi-affair-reception :deep(.modal-container) {
    max-height: calc(90vh - 200px);
}

#modal-multi-affair-reception :deep(.modal-buttons) {
    flex-wrap: wrap;
}

.totals-footer {
    flex-basis: 100%;
    border-top: 2px solid #e67e22;
    padding: 10px 20px;
    margin-bottom: 5px;
}

.totals-footer-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
    font-weight: bold;
    font-size: 14px;
    color: #2c3e50;
}
</style>

<template>
    <Modal id="modal-multi-affair-reception" :hide-icon="true">
        <template v-slot:modalTitle>
            <div v-if="isEditMode">MODIFIER UNE RÉCEPTION MULTI-AFFAIRES (R{{ receptionId }})</div>
            <div v-else>CRÉER UNE RÉCEPTION MULTI-AFFAIRES</div>
        </template>
        <template v-slot:modalContent>
            <form @submit.prevent="handleSubmit">
                <div class="form-row">
                    <BaseDatepicker
                        v-model="reception.date"
                        label="Date de réception"
                        :displayTime="true"
                        name="date"
                        :required="true"
                        :error="errors.date"
                        @onChange="clearError('date')"
                    />
                    <BaseSelect
                        v-model="reception.supplier"
                        label="Fournisseur"
                        name="supplier"
                        api="supplier/search"
                        fieldValue="id"
                        fieldLabel="name"
                        :searchable="true"
                        :required="true"
                        :minChars="1"
                        :error="errors.supplier"
                        @onChange="clearError('supplier')"
                    />
                </div>

                <div class="form-row">
                    <BaseInput
                        v-model="reception.shippingCosts"
                        label="Frais de port"
                        type="text"
                        validator="price"
                    />
                    <BaseInput
                        v-model="reception.packagingCosts"
                        label="Frais d'emballage"
                        type="text"
                        validator="price"
                    />
                </div>

                <div class="form-row" v-if="reception.supplier">
                    <BaseSelect
                        v-model="selectedOrder"
                        label="Charger depuis une commande"
                        name="order"
                        api="priceRequest/search"
                        :apiParams="{
                            orderedOnly: true,
                            supplierId: reception.supplier?.value || reception.supplier || -1,
                            priceRequestMode: 'order',
                        }"
                        fieldValue="id"
                        fieldLabel="orderNumber"
                        :searchable="true"
                        :minChars="2"
                        @onChange="onOrderSelected"
                    />
                </div>

                <div class="articles-section" style="flex-direction: column; align-items: stretch;">
                    <h3>Articles à réceptionner</h3>
                    <div class="article-selector">
                        <BaseSelect
                            v-model="currentArticle.article"
                            label="Article"
                            name="article"
                            api="article/search"
                            fieldValue="id"
                            fieldLabel="label"
                            :searchable="true"
                            :minChars="2"
                            placeholder="Rechercher un article"
                        />
                        <BaseSelect
                            v-model="currentArticle.affair"
                            label="Affaire"
                            name="affair"
                            api="affair/search"
                            :apiParams="{ withoutStatusFilter: true }"
                            fieldValue="id"
                            fieldLabel="name"
                            :searchable="true"
                            :minChars="2"
                            placeholder="Rechercher une affaire"
                        />
                        <BaseSelect
                            v-model="currentArticle.establishment"
                            label="Établissement"
                            name="establishment"
                            :defaultOptions="establishments"
                        />
                        <BaseButton
                            class="orange-button add-article-btn"
                            buttonText="Ajouter"
                            @click="addArticle"
                        >
                            <template v-slot:iconBefore>
                                <font-awesome-icon :icon="['fas', 'plus']" />
                            </template>
                        </BaseButton>
                    </div>

                    <table v-if="articles.length > 0" class="table-articles">
                        <thead>
                            <tr>
                                <th>Article</th>
                                <th>Affaire</th>
                                <th>Quantité reçue</th>
                                <th>Prix d'achat (€)</th>
                                <th>Établissement</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(article, index) in articles" :key="index">
                                <td>{{ article.articleLabel }}</td>
                                <td>
                                    <BaseSelect
                                        v-model="article.affairId"
                                        :name="'affair_' + index"
                                        api="affair/search"
                                        :apiParams="{ withoutStatusFilter: true }"
                                        fieldValue="id"
                                        fieldLabel="name"
                                        :searchable="true"
                                        :minChars="2"
                                        :defaultLabel="article.affairName"
                                        @onChange="(val) => onAffairChange(index, val)"
                                    />
                                </td>
                                <td>
                                    <BaseInput
                                        v-model="article.quantity"
                                        type="text"
                                        validator="decimal"
                                        :name="'quantity_' + index"
                                    />
                                </td>
                                <td>
                                    <BaseInput
                                        v-model="article.buyPrice"
                                        type="text"
                                        validator="price"
                                        :name="'buyPrice_' + index"
                                    />
                                </td>
                                <td>{{ getEstablishmentLabel(article.establishment) }}</td>
                                <td>
                                    <button type="button" class="delete-btn" @click="removeArticle(index)">
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="errors.articles" class="error-message">{{ errors.articles }}</div>
                </div>
            </form>
        </template>
        <template v-slot:modalButtons>
            <div v-if="articles.length > 0" class="totals-footer">
                <div class="totals-footer-row">
                    <span>Total</span>
                    <span>Quantité : {{ totalQuantity }}</span>
                    <span>{{ formatPrice(totalHT) }} €</span>
                </div>
            </div>
            <BaseButton class="white-button" buttonText="Annuler" @click="handleCancel">
                <template v-slot:iconBefore>
                    <font-awesome-icon :icon="['fas', 'times']" />
                </template>
            </BaseButton>
            <BaseButton
                class="orange-button"
                :buttonText="isEditMode ? 'Modifier la réception' : 'Créer la réception'"
                @click="handleSubmit"
            >
                <template v-slot:iconBefore>
                    <font-awesome-icon :icon="['fas', 'check']" />
                </template>
            </BaseButton>
        </template>
    </Modal>
</template>

<script>
import Modal from './Modal.vue';
import BaseButton from '../Base/BaseButton.vue';
import BaseInput from '../Base/BaseInput.vue';
import BaseSelect from '../Base/BaseSelect.vue';
import BaseDatepicker from '../Base/BaseDatepicker.vue';
import axios from 'axios';
import { format, parse } from 'date-fns';

export default {
    name: 'MultiAffairReceptionModal',
    components: {
        Modal,
        BaseButton,
        BaseInput,
        BaseSelect,
        BaseDatepicker
    },
    props: {
        establishments: {
            type: Array,
            required: true
        },
        receptionId: {
            type: Number,
            default: null
        }
    },
    emits: ['close', 'created', 'updated'],
    data() {
        return {
            reception: {
                date: new Date(),
                supplier: null,
                shippingCosts: '',
                packagingCosts: ''
            },
            selectedOrder: null,
            currentArticle: {
                article: null,
                affair: null,
                establishment: null
            },
            articles: [],
            errors: {},
            isEditMode: false,
            isLoading: false
        };
    },
    mounted() {
        if (this.receptionId) {
            this.loadReception();
        }
    },
    computed: {
        totalQuantity() {
            return this.articles.reduce((sum, article) =>
                sum + (parseFloat(article.quantity) || 0), 0
            );
        },
        totalArticlesHT() {
            return this.articles.reduce((sum, article) =>
                sum + ((parseFloat(article.quantity) || 0) * (parseFloat(article.buyPrice) || 0)), 0
            );
        },
        totalHT() {
            const articlesTotal = this.totalArticlesHT;
            const shipping = parseFloat(this.reception.shippingCosts) || 0;
            const packaging = parseFloat(this.reception.packagingCosts) || 0;
            return articlesTotal + shipping + packaging;
        }
    },
    methods: {
        async loadReception() {
            this.isLoading = true;
            try {
                const response = await axios.get(`reception/${this.receptionId}`, {
                    toastError: true,
                    showPreloader: true
                });
                const data = response.data;

                this.reception.date = data.date;
                this.reception.supplier = { value: data.supplier.id, label: data.supplier.name };
                
                // Charger les frais depuis data.fees
                if (data.fees) {
                    this.reception.shippingCosts = data.fees.shippingCosts != null ? String(data.fees.shippingCosts) : '';
                    this.reception.packagingCosts = data.fees.packagingCosts != null ? String(data.fees.packagingCosts) : '';
                }

                // Charger la commande associée si présente
                if (data.priceRequestId) {
                    this.selectedOrder = {
                        value: data.priceRequestId,
                        label: data.priceRequestOrderNumber || `C${data.priceRequestId}`
                    };
                }

                this.articles = (data.articles || []).map(article => ({
                    articleId: article.articleId || article.id,
                    articleLabel: article.label,
                    affairId: article.affairId
                        ? { value: article.affairId, label: article.affairNumber || article.affairName || '' }
                        : null,
                    affairName: article.affairNumber || article.affairName || '',
                    quantity: article.quantity != null ? String(article.quantity) : '1',
                    buyPrice: article.buyPrice != null ? String(article.buyPrice) : '',
                    establishment: article.establishment || null,
                    priceRequestToArticlesId: article.priceRequestToArticlesId || null
                }));

                this.isEditMode = true;
            } catch (error) {
                console.error('Erreur lors du chargement de la réception:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async onOrderSelected(order) {
            if (this.isLoading) return;
            if (!order?.value) return;

            const orderId = order.value.value ?? order.value;

            try {
                const response = await axios.get(
                    `priceRequest/listArticlesByPriceRequest?priceRequestIds=${orderId}`,
                    { toastError: true, showPreloader: true }
                );

                if (response.data && response.data.length > 0) {
                    for (const priceRequest of response.data) {
                        const affairId = priceRequest.affairId || null;
                        const affairName = priceRequest.affairNumber || priceRequest.affairName || '';

                        for (const article of priceRequest.articles) {
                            this.articles.push({
                                articleId: article.id,
                                articleLabel: article.label,
                                affairId: affairId,
                                affairName: affairName,
                                quantity: article.quantityRemaining ?? article.quantityOrdered ?? 1,
                                buyPrice: article.buyPrice || '',
                                establishment: article.establishment || null,
                                priceRequestToArticlesId: article.priceRequestToArticlesId || null
                            });
                        }
                    }
                    this.clearError('articles');
                }
            } catch (error) {
                console.error('Erreur lors du chargement des articles de la commande:', error);
            }
        },
        addArticle() {
            if (!this.currentArticle.article) {
                alert('Veuillez sélectionner un article');
                return;
            }
            if (!this.currentArticle.affair) {
                alert('Veuillez sélectionner une affaire');
                return;
            }
            if (!this.currentArticle.establishment) {
                alert('Veuillez sélectionner un établissement');
                return;
            }

            this.articles.push({
                articleId: this.currentArticle.article,
                articleLabel: this.currentArticle.article?.label || this.currentArticle.article?.data?.label || 'Article',
                affairId: this.currentArticle.affair,
                affairName: this.currentArticle.affair?.label || this.currentArticle.affair?.data?.name || 'Affaire',
                quantity: 1,
                buyPrice: '',
                establishment: this.currentArticle.establishment
            });

            this.currentArticle = {
                article: null,
                affair: null,
                establishment: null
            };

            this.clearError('articles');
        },
        removeArticle(index) {
            this.articles.splice(index, 1);
        },
        onAffairChange(index, val) {
            if (val && this.articles[index]) {
                this.articles[index].affairName = val.label || val.data?.name || '';
            }
        },
        getEstablishmentLabel(value) {
            const val = value?.value ?? value;
            const est = this.establishments.find(e => e.value === val);
            return est ? est.label : val;
        },
        formatPrice(price) {
            if (price === null || price === undefined) return '0.00';
            return parseFloat(price).toFixed(2);
        },
        clearError(field) {
            if (this.errors[field]) {
                delete this.errors[field];
            }
        },
        validate() {
            this.errors = {};

            if (!this.reception.date) {
                this.errors.date = 'La date est obligatoire';
            }
            if (!this.reception.supplier) {
                this.errors.supplier = 'Le fournisseur est obligatoire';
            }
            if (this.articles.length === 0) {
                this.errors.articles = 'Veuillez ajouter au moins un article';
            } else if (this.articles.some(a => !a.quantity || parseFloat(a.quantity) <= 0)) {
                this.errors.articles = 'Veuillez saisir une quantité valide pour chaque article';
            } else if (this.articles.some(a => !a.buyPrice || parseFloat(a.buyPrice) <= 0)) {
                this.errors.articles = 'Veuillez saisir un prix d\'achat valide pour chaque article';
            }

            return Object.keys(this.errors).length === 0;
        },
        async handleSubmit() {
            if (!this.validate()) {
                return;
            }

            try {
                const payload = {
                    date: this.reception.date instanceof Date
                        ? format(this.reception.date, 'dd/MM/yyyy HH:mm')
                        : this.reception.date,
                    supplierId: this.reception.supplier?.value ?? this.reception.supplier,
                    priceRequestId: this.selectedOrder?.value ?? this.selectedOrder ?? null,
                    articles: this.articles.map(article => ({
                        articleId: article.articleId?.value ?? article.articleId,
                        affairId: article.affairId?.value ?? article.affairId,
                        quantity: parseFloat(article.quantity) || 0,
                        buyPrice: parseFloat(article.buyPrice) || 0,
                        establishment: article.establishment?.value ?? article.establishment,
                        priceRequestToArticlesId: article.priceRequestToArticlesId || null
                    })),
                    shippingCosts: this.reception.shippingCosts ? parseFloat(this.reception.shippingCosts) : 0,
                    packagingCosts: this.reception.packagingCosts ? parseFloat(this.reception.packagingCosts) : 0
                };

                if (this.isEditMode) {
                    payload.receptionId = this.receptionId;
                    const response = await axios.put('reception/updateMultiAffair', payload, {
                        toastSuccessMessage: 'Réception multi-affaires modifiée avec succès',
                        toastError: true,
                        showPreloader: true
                    });
                    this.$emit('updated', response.data);
                } else {
                    const response = await axios.post('reception/createMultiAffair', payload, {
                        toastSuccessMessage: 'Réception multi-affaires créée avec succès',
                        toastError: true,
                        showPreloader: true
                    });
                    this.$emit('created', response.data);
                }
                this.handleCancel();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde de la réception:', error);
            }
        },
        handleCancel() {
            this.reception = {
                date: new Date(),
                supplier: null,
                shippingCosts: '',
                packagingCosts: ''
            };
            this.selectedOrder = null;
            this.currentArticle = {
                article: null,
                affair: null,
                establishment: null
            };
            this.articles = [];
            this.errors = {};
            this.$emit('close');
        }
    }
};
</script>
